import { Injectable, inject, signal, computed, effect, Signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, of, catchError, map, EMPTY } from 'rxjs';
import { faro } from '@grafana/faro-web-sdk';

import { User, UserRole } from '@app/core/models';
import { ApiResponse } from '@app/core/types';
import { environment } from '@env/environment';

/**
 * CW-13d-admin — cookie-auth AuthService rewrite (AW-12 HIGH).
 *
 * Closes the AW-12 in-memory-token gap: there is no JWT in JS at all. The
 * browser-managed `tb_admin_access` HttpOnly cookie carries the session.
 * AuthService only tracks the current admin profile (via `/admin/auth/me`).
 *
 * Removed surface (intentional — must not be re-added):
 *   - `private token` signal
 *   - `getToken()` / `setToken()`
 *   - `LoginResponse` interface (no `{ token }` field in login response)
 *   - All persistent browser storage reads/writes (C-1 regression — no
 *     token to persist anywhere).
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly currentUser = signal<User | null>(null);

  /**
   * Reactive authentication state. `true` whenever an admin profile is
   * loaded in memory. In cookie mode the only authoritative session check
   * is server-side (`GET /admin/auth/me`), so consumers calling this
   * without first running `rehydrateUser()` will see `false` after a hard
   * page reload until rehydration completes.
   */
  readonly isAuthenticated: Signal<boolean> = computed(() => this.currentUser() !== null);

  /**
   * Mirrors the current admin into Faro's session context so errors, traces,
   * and events can be traced to a specific operator. Tracks login, hard-reload
   * rehydration, and logout from the single `currentUser` source of truth.
   *
   * Only `id` + `role` are sent — never email/name — to keep PII out of
   * telemetry (security rule: no PII in browser console/URLs/telemetry).
   * `faro.api?.` is null-safe before Faro initialization completes.
   */
  private readonly faroUserSync = effect(() => {
    const user = this.currentUser();
    if (user) {
      faro.api?.setUser({ id: user.id, attributes: { role: user.role } });
    } else {
      faro.api?.resetUser();
    }
  });

  /**
   * POST /admin/auth/login. Server sets the `tb_admin_access` HttpOnly
   * cookie via `Set-Cookie`. Response body is the admin profile.
   *
   * `withCredentials: true` is added globally by `authInterceptor`.
   */
  login(email: string, password: string): Observable<User> {
    return this.http
      .post<ApiResponse<User>>(`${environment.apiUrl}/admin/auth/login`, { email, password })
      .pipe(
        map((res) => res.data),
        tap((user) => this.currentUser.set(user)),
      );
  }

  /**
   * POST /admin/auth/logout. Server clears the cookie. We always clear the
   * in-memory user — even if the HTTP call fails (best-effort logout).
   */
  logout(): Observable<void> {
    return this.http
      .post<ApiResponse<void>>(`${environment.apiUrl}/admin/auth/logout`, {})
      .pipe(
        map(() => undefined),
        tap(() => this.clearSession()),
        catchError(() => {
          // Defensive: regardless of server-side state, clear local state.
          this.clearSession();
          return EMPTY;
        }),
      );
  }

  /**
   * GET /admin/auth/me. Used on app boot / route activation to hydrate the
   * profile when the cookie is present but the in-memory signal is empty
   * (e.g. after a hard refresh). Returns `true` on success, `false` on
   * 401 (cookie missing/expired) or any other failure.
   */
  rehydrateUser(): Observable<boolean> {
    if (this.currentUser()) {
      return of(true);
    }

    return this.http
      .get<ApiResponse<User>>(`${environment.apiUrl}/admin/auth/me`)
      .pipe(
        tap((res) => this.currentUser.set(res.data)),
        map(() => true),
        catchError(() => {
          this.clearSession();
          return of(false);
        }),
      );
  }

  /** In-memory only — no storage to clear. */
  clearSession(): void {
    this.currentUser.set(null);
  }

  getCurrentUser(): Signal<User | null> {
    return this.currentUser.asReadonly();
  }

  getUser(): User | null {
    return this.currentUser();
  }

  setUser(user: User | null): void {
    this.currentUser.set(user);
  }

  getUserRole(): UserRole | null {
    return this.currentUser()?.role ?? null;
  }

  /**
   * Convenience method invoked by the sidebar / header sign-out button.
   * Calls logout() and (regardless of result) navigates to /auth/login.
   */
  signOut(): void {
    this.logout().subscribe({
      complete: () => {
        this.router.navigate(['/auth/login']);
      },
    });
  }
}
